import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import { 
  type PaymentAgreement,
  getAgreementStatusLabel,
  getAgreementStatusColor,
  getAgreementReasonLabel,
} from '@/hooks/finance/usePaymentAgreements';
import { CustomInstallmentsViewer } from './CustomInstallmentsViewer';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

interface PreviousRenegotiationCardProps {
  agreement: PaymentAgreement;
}

export function PreviousRenegotiationCard({ agreement }: PreviousRenegotiationCardProps) {
  const installmentAmount = agreement.num_installments > 0 
    ? agreement.agreed_amount / agreement.num_installments 
    : 0;

  const hasCustomInstallments = agreement.custom_installments && agreement.custom_installments.length > 0;

  return (
    <div className="border rounded-lg p-3 bg-amber-50/50 border-amber-200">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-amber-700">
          {agreement.agreement_number - 1}ª Renegociação
        </p>
        <Badge 
          variant="outline" 
          className={`text-xs ${getAgreementStatusColor(agreement.status)}`}
        >
          {getAgreementStatusLabel(agreement.status)}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Valor:</span>{' '}
          <span className="font-medium">{formatCurrency(agreement.agreed_amount)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Estrutura:</span>{' '}
          {hasCustomInstallments ? (
            <span className="font-medium">
              {agreement.num_installments}x (personalizado)
            </span>
          ) : (
            <span className="font-medium">
              {agreement.num_installments}x de {formatCurrency(installmentAmount)}
            </span>
          )}
        </div>
        {!hasCustomInstallments && agreement.first_due_date && (
          <div>
            <span className="text-muted-foreground">Início:</span>{' '}
            <span className="font-medium">
              {format(parseISO(agreement.first_due_date), 'dd/MM/yyyy', { locale: ptBR })}
            </span>
          </div>
        )}
        {agreement.reason && (
          <div>
            <span className="text-muted-foreground">Motivo:</span>{' '}
            <span className="font-medium">{getAgreementReasonLabel(agreement.reason)}</span>
          </div>
        )}
      </div>

      {/* Custom Installments Table */}
      {hasCustomInstallments && (
        <CustomInstallmentsViewer 
          installments={agreement.custom_installments!} 
          currency="USD" 
        />
      )}
      
      {agreement.created_at && (
        <p className="text-xs text-muted-foreground mt-2">
          Criado em {format(parseISO(agreement.created_at), "dd/MM/yyyy", { locale: ptBR })}
          {agreement.requester_name && ` por ${agreement.requester_name}`}
        </p>
      )}
    </div>
  );
}
